import { forwardRef, Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { filesApi, miniappApi } from '@papan/files/client'
import { useIdentitySwitchEpoch } from '@papan/files/client/queries'
import { getActiveTenantId } from '@papan/api-client'
import { presentsAsMiniapp } from '@papan/types'
import { usePreviewSession } from '../client/usePreviewSession.js'
import { HtmlViewer, type HtmlViewerHandle, type OpenFileTargetWire, type OpenPdappTargetWire, type OpenFileOptsWire } from './HtmlViewer.js'
import { useAppRunNoticeToast } from './useAppRunNoticeToast.js'
import { PreviewConsentBanner } from './PreviewConsentBanner.js'
import { MiniAppInstallGate } from './MiniAppInstallGate.js'
import { MiniAppNoServeView } from './MiniAppNoServeView.js'
import { frameMessageTarget } from './app-origin-frame.js'

/**
 * MiniAppRunView — the RUNNING mini-app embedded inside the MiniAppEditor tab
 * (the runner face of the multi-purpose editor, alongside the settings face).
 *
 * It is the same runtime AppWindow uses (usePreviewSession mints/refreshes the
 * sandboxed preview session; HtmlViewer owns the iframe + SSE reload + bridges),
 * but WITHOUT the shell chrome (no ShellTopBarPortal / AppTopBar / hero card) —
 * the editor toolbar supplies the frame.
 *
 * Two modes, matching AppWindow's two branches:
 *   - kind='miniapp' (default) — resolves the app's index.html entry from the
 *     `.miniapp` folder, gated on install/consent.
 *   - kind='html' — serves a standalone HTML FILE directly (Mode A); no folder
 *     listing, no install gate. Used by the mobile app-run surface so loose
 *     HTML runs in the same chromeless runtime as a mini-app.
 */
type Resolved =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  // `fullPath` is set for Mode A (html) so a file below the session root
  // resolves to a root-relative entry (mirrors AppWindow); null for mini-apps.
  | { state: 'ready'; entryFileId: string; entryFileName: string; parentId: string | null; fullPath?: string | null }
  // No index.html to serve, but the app runs background work (triggers can fire
  // without a frontend). Show what it runs.
  | { state: 'tasks'; triggered: number; scheduled: number }
  // No index.html and no background work — nothing to run yet.
  | { state: 'empty' }

/** The `papan.openFile*` bridge handler shape (matches HtmlViewer's prop). */
type OpenFileTargetHandler = (
  target: OpenFileTargetWire,
  ctx: { appFolderId: string | null; opts: OpenFileOptsWire },
) => void

/** Imperative controls a host (e.g. the mobile app-control panel) drives on the
 *  running app. `reload` is owned by the HOST (remount) — this exposes only the
 *  in-frame history nav, which must reach the LIVE iframe (a remount would lose
 *  the app's history). Back/forward no-op when the tracked history has nowhere
 *  to go (see MiniAppNavState). */
export interface MiniAppRunHandle {
  /** Soft-reload the running app — forwards to HtmlViewer's double-buffered
   *  reload (same path the SSE auto-reload uses), so a manual Refresh doesn't
   *  remount/re-mint the session. */
  reload: () => void
  /** Snapshot the running app's current page (script-free HTML, assets inlined)
   *  for export. Resolves null when no iframe is live. */
  requestSnapshot: () => Promise<string | null>
  /** Browser-history back inside the app iframe (posts `papan:nav:back`). */
  back: () => void
  /** Browser-history forward inside the app iframe (posts `papan:nav:forward`). */
  forward: () => void
}

/** In-app navigation state, modeled parent-side from the iframe's `papan:nav`
 *  reports (a cross-origin iframe can't be queried for `history.length`, so we
 *  track a virtual index the way HtmlEditor does). Index 0 = the entry page. */
export interface MiniAppNavState {
  /** The app is on some page other than its entry (drives Refresh↔Home). */
  hasNavigated: boolean
  /** There is a prior page to go back to. */
  canBack: boolean
  /** The user went back and can move forward again. */
  canForward: boolean
}

interface MiniAppRunViewProps {
  /** The hosted ref — a `.miniapp` folder id (Mode B) or an HTML file id (Mode A). */
  folderId: string
  folderName?: string
  /** Mode B ('miniapp', default): resolve index.html from the folder + gate on
   *  install. Mode A ('html'): serve the file at `folderId` directly, no gate. */
  kind?: 'miniapp' | 'html'
  /** Reload the preview when a source file changes (SSE `__reload`). Defaults to
   *  `true` (HtmlViewer's own default). The editor threads the app's persisted
   *  preference here so a running app honors the Settings toggle live. */
  autoRefresh?: boolean
  /** Host-supplied openFile handler. When the runner lives inside a WORKSPACE
   *  tab (MiniAppEditor), the host passes the workspace's resolver so openFile
   *  opens the target as a tab IN this workspace — staying in the workspace,
   *  not spawning a new browser tab. Absent (standalone use) → the local
   *  new-browser-tab fallback below. */
  onOpenFileTarget?: OpenFileTargetHandler
  /** #788 — LAUNCH another PDApp. */
  onOpenPdappTarget?: (target: OpenPdappTargetWire, ctx: { opts: OpenFileOptsWire }) => void
  /** Reports the in-app navigation state (entry/back/forward availability) so a
   *  host can toggle Refresh↔Home and enable/disable back/forward (mobile
   *  app-control panel). */
  onNavStateChange?: (state: MiniAppNavState) => void
}

export const MiniAppRunView = forwardRef<MiniAppRunHandle, MiniAppRunViewProps>(function MiniAppRunView(
  { folderId, folderName, kind = 'miniapp', autoRefresh, onOpenFileTarget, onOpenPdappTarget, onNavStateChange },
  ref,
) {
  const inner = (
    <MiniAppRunInner ref={ref} refId={folderId} kind={kind} autoRefresh={autoRefresh} onOpenFileTarget={onOpenFileTarget} onOpenPdappTarget={onOpenPdappTarget} onNavStateChange={onNavStateChange} />
  )
  // #1316 review round 1 (owner, final): an org-email switch remounts the runner (gate included), keyed like
  // TabContent — it was resolved and its session minted as the previous identity, and nothing in it re-asks.
  const identityEpoch = useIdentitySwitchEpoch()
  // Mode B gates the run on install (the consent point) — an uninstalled app
  // can be previewed but isn't "live" (no principal / triggers) until installed.
  // Mode A (standalone HTML) has no install concept → run as-is.
  return kind === 'miniapp'
    ? <MiniAppInstallGate key={identityEpoch} folderId={folderId} folderName={folderName}>{inner}</MiniAppInstallGate>
    : <Fragment key={identityEpoch}>{inner}</Fragment>
})

const MiniAppRunInner = forwardRef<MiniAppRunHandle, { refId: string; kind: 'miniapp' | 'html'; autoRefresh?: boolean; onOpenFileTarget?: OpenFileTargetHandler; onOpenPdappTarget?: (target: OpenPdappTargetWire, ctx: { opts: OpenFileOptsWire }) => void; onNavStateChange?: (state: MiniAppNavState) => void }>(function MiniAppRunInner(
  { refId, kind, autoRefresh = true, onOpenFileTarget, onOpenPdappTarget, onNavStateChange },
  ref,
) {
  const [resolved, setResolved] = useState<Resolved>({ state: 'loading' })
  // Live iframe handle — back/forward post history messages to it; a remount
  // (the host's reload) would drop the app's in-frame history, so nav must go
  // through the mounted iframe, not a re-render.
  const viewerRef = useRef<HtmlViewerHandle>(null)
  // #184 Phase 4. Harmless on a share session: the server attaches a notice only
  // for a caller with readwrite on the app folder, which a link visitor is not.
  const onAppRunNotice = useAppRunNoticeToast()

  useEffect(() => {
    let cancelled = false
    const fail = (message: string) => { if (!cancelled) setResolved({ state: 'error', message }) }
    setResolved({ state: 'loading' })
    filesApi.getFileInfo(refId)
      .then(async info => {
        if (cancelled) return
        // Mode A — run the HTML file itself; the session roots at its serve
        // folder and the entry is the file (made root-relative via fullPath).
        if (kind === 'html') {
          if (info.isFolder || !/\.html?$/i.test(info.name)) return fail('This item is not an HTML page.')
          setResolved({ state: 'ready', entryFileId: refId, entryFileName: info.name, parentId: info.parentId ?? null, fullPath: info.path ?? null })
          return
        }
        // (#327) A folder-target `.shared` mount of someone else's `.miniapp` is
        // runnable: `listChildren` below synthesizes its children as compound
        // mount ids, and the session mint resolves those to the SOURCE app
        // folder — so the app boots as the source principal with the recipient
        // as actor. Kind-gated, so a hand-made `X.miniapp.shared` folder still fails.
        // The folder test lives inside `presentsAsMiniapp` — a `.shared` pointer is
        // a FILE row presenting as a folder, so an outer `!info.isFolder` rejects it.
        if (!presentsAsMiniapp(info.name, info.fileType, info.isFolder)) return fail('This item is not a PDApp.')
        // Entry point: index.html at the app folder's root.
        const listing = await filesApi.listChildren(refId, { limit: 200 })
        const { files } = listing
        if (cancelled) return
        // (#327) An unresolvable mount lists empty — report the DENIAL, not an
        // empty app (see AppWindow for the same gate).
        if (listing.mount?.state === 'broken' || listing.parentPermission === 'hidden' || listing.parentPermission === 'none') {
          return fail('You don’t have access to this app. Ask the owner to share it with you.')
        }
        const entry = files.find(f => !f.isFolder && f.name.toLowerCase() === 'index.html')
        if (!entry) {
          // Same denial check AppWindow makes, and for the same reason: an app the
          // caller cannot read lists EMPTY, and `parentPermission` above reports
          // `readcopy` for a hidden folder whenever they hold a grant the ancestor
          // whitelist covers. Without this, MOBILE (which routes through here) tells
          // the person the author's app is unfinished when the truth is that it is
          // not theirs to open — exactly the mis-blame #788 makes common.
          try {
            const perm = await filesApi.getPermission(refId)
            if (cancelled) return
            if (perm === 'hidden' || perm === 'none') {
              return fail('You don’t have access to this app. Ask the owner to share it with you.')
            }
          } catch (e) {
            // `GET /permission` answers 403 for `none` — the real denial arrives
            // HERE, not as a value, so the status has to be read or this catch
            // reports "check your connection" for the commonest case there is.
            if (cancelled) return
            const status = (e as { status?: number }).status
            return fail(status === 403 || status === 404
              ? 'You don’t have access to this app. Ask the owner to share it with you.'
              : 'Couldn’t check your access to this app. Check your connection and try again.')
          }
          // No page to serve — but the app can still run background tasks. Count
          // its declared triggers (scheduled = a future `schedule` source; the
          // rest are event-driven "triggered" tasks).
          let triggered = 0, scheduled = 0
          try {
            const { triggers } = await miniappApi.getTriggers(refId)
            for (const t of triggers) {
              if ((t?.when as { source?: string } | undefined)?.source === 'schedule') scheduled++
              else triggered++
            }
          } catch { /* no readable triggers */ }
          if (cancelled) return
          return setResolved(triggered + scheduled > 0 ? { state: 'tasks', triggered, scheduled } : { state: 'empty' })
        }
        setResolved({ state: 'ready', entryFileId: entry.id, entryFileName: entry.name, parentId: refId })
      })
      .catch(() => fail('App not found — it may have been deleted or you may not have access.'))
    return () => { cancelled = true }
  }, [refId, kind])

  const ready = resolved.state === 'ready'
  const {
    baseUrl,
    appOrigin,
    previewOrigin,
    currentRoot,
    pendingConsent,
    approveConsent,
    error: sessionError,
  } = usePreviewSession({
    fileId: ready ? resolved.entryFileId : '',
    mode: 'preview',
    enabled: ready,
    // #1408: run on the app's own preview origin where the host offers one (camera, microphone).
    appOrigin: true,
  })

  // The app's default entry, relative to the session root (mirrors AppWindow).
  const defaultEntryRel = useMemo(() => {
    if (!ready) return null
    if (currentRoot?.entryPath) return currentRoot.entryPath
    if (pendingConsent?.entryPath) return pendingConsent.entryPath
    // Mode A: the HTML file may sit BELOW the session root — resolve it
    // root-relative so the iframe loads the right page (AppWindow's html branch).
    if (resolved.fullPath && currentRoot?.rootPath) {
      const root = currentRoot.rootPath.replace(/\/+$/, '')
      if (resolved.fullPath.startsWith(root + '/')) return resolved.fullPath.slice(root.length + 1)
    }
    return resolved.entryFileName
  }, [ready, resolved, currentRoot?.entryPath, currentRoot?.rootPath, pendingConsent?.entryPath])

  const entryRel = defaultEntryRel || undefined
  // The folder the app is SERVED from — drives the Papan-bar embed + consent.
  const appRunFolderId = currentRoot?.rootId ?? (ready ? resolved.parentId : null)
  // Debug-log target: Mode B → the `.miniapp` folder; Mode A → the HTML file.
  const debugLogTarget = useMemo<{ kind: 'miniapp' | 'html'; id: string } | null>(() => {
    if (resolved.state !== 'ready') return null
    if (kind === 'html') return { kind: 'html', id: resolved.entryFileId }
    return appRunFolderId ? { kind: 'miniapp', id: appRunFolderId } : null
  }, [resolved, kind, appRunFolderId])

  // papan.openFile* FALLBACK — used only when no host handler is supplied
  // (standalone use, no workspace around us). Resolve the target and open it in
  // a NEW reusable browser tab — a file → single-file view, a folder → the
  // drive. Inside a workspace tab the host passes `onOpenFileTarget` instead
  // (opens as a workspace tab), and this fallback is not used.
  const fallbackOpenFileTarget = useCallback(async (
    target: OpenFileTargetWire,
    ctx: { appFolderId: string | null; opts: OpenFileOptsWire },
  ) => {
    try {
      let fileId: string
      let isFolder = false
      if (target.kind === 'fileId') {
        fileId = target.fileId
        // #788 — a `.pdapp` FOLDER id opens the FOLDER, the same answer mobile
        // and share view already gave. Launching an app is `openPdappByFolderId`.
        // `null` = could not tell even after a retry. Falling back to "file" keeps
        // the recoverable direction (a file view of a folder errors; a wrong
        // launch RUNS someone's app) — see `isFolderById`.
        isFolder = (await filesApi.isFolderById(fileId)) ?? false
      } else {
        const result = target.kind === 'relativePath'
          ? await filesApi.resolvePath(target.path, ctx.appFolderId ?? undefined)
          : await filesApi.resolvePath(`/papan/${target.path.replace(/^\/+/, '')}`)
        if (!result?.id) return
        fileId = result.id
        isFolder = result.file.isFolder
      }
      const m = window.location.pathname.match(/^(\/t\/[^/]+|\/~)/)
      const prefix = m ? m[1] : '/~'
      // The SAME reusable-tab keys every other surface uses (`papan:file:<id>` /
      // `papan:folder:<id>`). A scheme of this module's own splits one file across
      // two browser tabs depending on which surface opened it.
      //
      // The `getFileInfo` round trip above sits in front of this `window.open`.
      // That is tolerable because this runs in a postMessage handler, where there
      // is no transient user activation to lose in the first place — the popup
      // either opens or it does not, exactly as before #788.
      if (isFolder) {
        window.open(`${prefix}/folder/${encodeURIComponent(fileId)}`, `papan:folder:${fileId}`)
      } else {
        window.open(`${prefix}/file/${encodeURIComponent(fileId)}`, `papan:file:${fileId}`)
      }
    } catch { /* not found / no access — fire-and-forget, no oracle back to the app */ }
  }, [])

  /** #788 — LAUNCH another app when no host handler is wired: a new reusable
   *  browser tab on the app route, the same shape as the file fallback above.
   *  The route resolves and gates the target as the VIEWER, so a target they
   *  cannot open refuses there and this app is told nothing either way. */
  const fallbackOpenPdappTarget = useCallback((target: OpenPdappTargetWire) => {
    try {
      // The TARGET's tenant decides the prefix, as it does on every other surface
      // — deriving it from the current URL sent a cross-tenant launch to
      // `/~/app/<foreign id>`, which dead-ends.
      const m = window.location.pathname.match(/^(\/t\/[^/]+|\/~)/)
      const here = m ? m[1] : '/~'
      const prefix = target.tenantId === getActiveTenantId() ? here : `/t/${encodeURIComponent(target.tenantId)}`
      window.open(`${prefix}/app/${encodeURIComponent(target.folderId)}`, `papan:app:${target.folderId}`)
    } catch { /* fire-and-forget, no oracle back to the app */ }
  }, [])

  // Prefer the host's resolver (workspace tab → opens IN the workspace); fall
  // back to the new-browser-tab behavior when running standalone.
  const effectiveOpenFileTarget = onOpenFileTarget ?? fallbackOpenFileTarget
  const effectiveOpenPdappTarget = onOpenPdappTarget ?? fallbackOpenPdappTarget

  // Model in-app navigation parent-side so the host can flip Refresh↔Home AND
  // enable/disable back/forward. A cross-origin iframe can't be queried for
  // `history.length`, so we track a VIRTUAL index off the `papan:nav` reports,
  // the same shape HtmlEditor uses (index 0 = entry page). `navFromButton`
  // marks the nav a back/forward BUTTON caused, so its echoed report doesn't
  // re-push the history. Refs keep the callback + handle stable across renders.
  const defaultEntryRelRef = useRef(defaultEntryRel)
  defaultEntryRelRef.current = defaultEntryRel
  const onNavStateChangeRef = useRef(onNavStateChange)
  onNavStateChangeRef.current = onNavStateChange
  const navIndexRef = useRef(0)
  const navLenRef = useRef(1)
  const navFromButtonRef = useRef(false)
  const reportNav = useCallback(() => {
    onNavStateChangeRef.current?.({
      hasNavigated: navIndexRef.current > 0,
      canBack: navIndexRef.current > 0,
      canForward: navIndexRef.current < navLenRef.current - 1,
    })
  }, [])
  const handleNavigate = useCallback((url: string) => {
    // Cross-origin nav lands outside our preview origin (HtmlViewer's probe
    // sends the `about:external` sentinel); it's off our history, skip it.
    if (url === 'about:external') return
    // A back/forward button already moved the index; its echoed report is a
    // no-op for the model.
    if (navFromButtonRef.current) { navFromButtonRef.current = false; return }
    let rel = ''
    try { const m = new URL(url).pathname.match(/^\/s\/[^/]+\/(.*)$/); rel = m ? decodeURIComponent(m[1]) : '' }
    catch { /* not a URL — treat as a real navigation below */ }
    if (!rel || rel === defaultEntryRelRef.current) {
      // Entry page (initial load / refresh / Home) → reset to the root.
      navIndexRef.current = 0
      navLenRef.current = 1
    } else {
      // A genuine push — truncate any forward history past the current point.
      navIndexRef.current += 1
      navLenRef.current = navIndexRef.current + 1
    }
    reportNav()
  }, [reportNav])
  useImperativeHandle(ref, () => ({
    reload: () => viewerRef.current?.reload(),
    requestSnapshot: () => viewerRef.current?.requestSnapshot() ?? Promise.resolve(null),
    back: () => {
      if (navIndexRef.current <= 0) return
      navFromButtonRef.current = true
      viewerRef.current?.iframe?.contentWindow?.postMessage({ type: 'papan:nav:back' }, frameMessageTarget(viewerRef.current?.iframe))
      navIndexRef.current -= 1
      reportNav()
    },
    forward: () => {
      if (navIndexRef.current >= navLenRef.current - 1) return
      navFromButtonRef.current = true
      viewerRef.current?.iframe?.contentWindow?.postMessage({ type: 'papan:nav:forward' }, frameMessageTarget(viewerRef.current?.iframe))
      navIndexRef.current += 1
      reportNav()
    },
  }), [reportNav])

  if (resolved.state === 'loading') {
    return <div className="flex-1 flex items-center justify-center themed-text-muted text-sm">Starting app…</div>
  }
  if (resolved.state === 'error') {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-lg themed-text">Can’t run this app</p>
          <p className="text-sm themed-text-muted">{resolved.message}</p>
        </div>
      </div>
    )
  }
  if (resolved.state === 'tasks') {
    return <MiniAppNoServeView triggered={resolved.triggered} scheduled={resolved.scheduled} />
  }
  if (resolved.state === 'empty') {
    return <MiniAppNoServeView triggered={0} scheduled={0} />
  }
  if (pendingConsent) {
    return (
      <PreviewConsentBanner
        proposedRootPath={pendingConsent.rootPath}
        proposedRootId={pendingConsent.rootId}
        browseRootId={pendingConsent.browseRootId}
        fileId={resolved.entryFileId}
        previewOrigin={previewOrigin}
        entryFilePath={defaultEntryRel ?? undefined}
        onAllow={approveConsent}
      />
    )
  }
  if (sessionError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-lg themed-text">Can’t run this app</p>
          <p className="text-sm themed-text-muted">{sessionError}</p>
        </div>
      </div>
    )
  }
  if (baseUrl && entryRel) {
    return (
      <HtmlViewer
        ref={viewerRef}
        content=""
        baseUrl={baseUrl}
        entryFile={entryRel}
        previewFileId={resolved.entryFileId}
        parentId={appRunFolderId}
        autoRefresh={autoRefresh}
        debugLogTarget={debugLogTarget}
        onNavigate={handleNavigate}
        onOpenFileTarget={effectiveOpenFileTarget}
        onOpenPdappTarget={effectiveOpenPdappTarget}
        onAppRunNotice={onAppRunNotice}
        accountsContext="app"
        appOrigin={appOrigin}
      />
    )
  }
  return <div className="flex-1 flex items-center justify-center themed-text-muted text-sm">Starting app…</div>
})
